import { useRouteMatch } from '@shared/core';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';

const WEBSITE = 'website';
const PAGE = 'page';

/**
 * To get current page path/slug
 * @param eventHandle - To get current page path/slug for event
 * @param isAdminPage - To get current page path/slug for admin / guest site
 */
export function useCurrentPageSlug(eventHandle: string, isAdminPage?: boolean) {
  const matchRoute = isAdminPage ? `/${eventHandle}/edit/${WEBSITE}/${PAGE}/:pageSlug` : `/${eventHandle}/${PAGE}/:pageSlug`;

  const routeMatch = useRouteMatch<{ pageSlug: string }>(`/${eventHandle}/:pageSlug`);
  const routeMatchCustomPage = useRouteMatch<{ pageSlug: string }>(matchRoute);

  const { isMigratedCustomPageEnabled } = useMigratedCustomPageFeatureFlags();

  const isUrlBasedCustomPageSlug = routeMatch?.params.pageSlug === PAGE;
  // Strip query parameters that may leak into the pathname when history.push/replace
  // is called with a URL containing query params as the pathname (e.g., goToPath returns
  // "/event/page?ctx=websiteEditor&..." and callers pass it as { pathname: ... }).
  const rawSlug = isUrlBasedCustomPageSlug ? routeMatchCustomPage?.params.pageSlug : routeMatch?.params.pageSlug;
  const currentPageSlug = rawSlug?.split('?')[0] || undefined;

  // For guest site routes (not admin), check if the current slug is a migrated custom page
  // when the feature flag is enabled. This allows routes like /${eventHandle}/story to be
  // treated as custom pages instead of requiring /${eventHandle}/page/story.
  const isMigratedSlug = !isAdminPage && !!currentPageSlug && isMigratedCustomPageEnabled(currentPageSlug);

  const isCustomPageSlug = isUrlBasedCustomPageSlug || isMigratedSlug;

  return { currentPageSlug, isCustomPageSlug };
}
